import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { getAliceAgentApp, getAliceAgentAppUserAgreements } from 'client/api/services/aliceAgentApps';
import { AliceAgentAppIntegrationsPage } from 'client/blocks/AliceAgentAppIntegrationsPage/AliceAgentAppIntegrationsPage';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { routes } from 'client/lib/routes';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import { sortUserAgreements, UserAgreement } from 'client/model/userAgreement';
import { getIsAdmin } from 'client/utils';
import { buildCorrectedAppPath, getAliceAppType } from 'client/utils/aliceAppRoute';
import * as keyset from 'i18n/AliceExternalAppForm';

import { AliceAgentAppSharingPage } from '../AliceAgentAppSharingPage/AliceAgentAppSharingPage';

import { AliceExternalAdminSettings } from './AliceExternalAdminSettings';
import { AliceExternalAppAccountLinking } from './AliceExternalAppAccountLinking';
import { AliceExternalAppEditPage } from './AliceExternalAppEditPage';
import { AliceExternalAppLayout } from './AliceExternalAppLayout';

const t = i18n(keyset);

interface Props {
    skillId: string;
}

const PROCESSING_POLL_INTERVAL_MS = 3000;

/**
 * Loads the External-agent DTO, owns refresh + status polling. While the draft sits in
 * `mcpProcessing` we re-fetch every ~3s so the form picks up the post-worker manifest without
 * a page reload.
 *
 * The page chrome (sidebar, version switcher, header, banners) lives in
 * `<AliceExternalAppLayout>` — `<Routes>` is rendered inside it so navigating between
 * Settings and Authorization swaps only the inner content (mirrors `AliceSkillAppPage`).
 */
export const AliceExternalAppPageContainer: FC<Props> = ({ skillId }) => {
    const { add: toasterAdd } = useToaster();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const [app, setApp] = useState<AliceAgentAppDTO>();
    const [userAgreements, setUserAgreements] = useState<UserAgreement[]>([]);
    const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isDraftView = !location.pathname.includes('/published/');

    const fetchApp = useCallback(async () => {
        try {
            const [next, agreements] = await Promise.all([
                getAliceAgentApp(skillId),
                getAliceAgentAppUserAgreements(skillId, isDraftView),
            ]);
            setApp(next);
            setUserAgreements(sortUserAgreements(agreements));
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                    ? err.message
                    : t('toast-load-error');
            toasterAdd({ title: message, theme: 'danger', name: 'LOAD_ALICE_EXTERNAL_APP' });
        } finally {
            setIsLoading(false);
        }
    }, [skillId, isDraftView, toasterAdd]);

    useEffect(() => {
        fetchApp();
    }, [fetchApp]);

    useEffect(() => {
        if (!app) return;
        if (app.draft.status !== 'mcpProcessing') {
            if (pollTimer.current) {
                clearTimeout(pollTimer.current);
                pollTimer.current = null;
            }
            return;
        }
        pollTimer.current = setTimeout(fetchApp, PROCESSING_POLL_INTERVAL_MS);
        return () => {
            if (pollTimer.current) {
                clearTimeout(pollTimer.current);
                pollTimer.current = null;
            }
        };
    }, [app, fetchApp]);

    if (isLoading && !app) return <PageLoader />;
    if (!app) return <Navigate to={routes.app.aliceApps} replace />;

    const correctedPath = buildCorrectedAppPath(location.pathname, getAliceAppType(app));
    if (correctedPath && correctedPath !== location.pathname) {
        return <Navigate to={correctedPath} replace />;
    }

    // Account-linking remains live for a published agent, so its reviewed draft stays editable.
    // Unknown and MCP-processing statuses remain locked by an explicit allowlist.
    const accountLinkingDisabled = app.draft.status !== 'inDevelopment' && app.draft.status !== 'reviewApproved';

    const isAdmin = getIsAdmin();

    return (
        <AliceExternalAppLayout app={app} isDraft={isDraftView} onSaved={fetchApp}>
            <Routes>
                <Route
                    path="draft/settings"
                    element={
                        <AliceExternalAppEditPage
                            app={app}
                            isDraft
                            fetchedUserAgreements={userAgreements}
                            onSaved={fetchApp}
                        />
                    }
                />
                <Route
                    path="draft/integrations"
                    element={
                        isAdmin ? (
                            <AliceAgentAppIntegrationsPage app={app} isDraft onSaved={fetchApp} />
                        ) : (
                            <Navigate to="../draft/settings" replace />
                        )
                    }
                />
                <Route
                    path="authorization"
                    element={
                        <AliceExternalAppAccountLinking
                            app={app}
                            onSaved={fetchApp}
                            disabled={accountLinkingDisabled}
                        />
                    }
                />
                {/* Backward-compat: account linking is non-versioned, the tab moved out of draft/. */}
                <Route
                    path="draft/authorization"
                    element={<Navigate to={routes.app.aliceExternalAppAuthorization(app.skillId)} replace />}
                />
                <Route path="admin-settings" element={<AliceExternalAdminSettings app={app} onSaved={fetchApp} />} />
                <Route
                    path="published/settings"
                    element={
                        app.published ? (
                            <AliceExternalAppEditPage
                                app={app}
                                isDraft={false}
                                fetchedUserAgreements={userAgreements}
                                onSaved={fetchApp}
                            />
                        ) : (
                            <Navigate to="../draft/settings" replace />
                        )
                    }
                />
                <Route
                    path="published/integrations"
                    element={
                        app.published && isAdmin ? (
                            <AliceAgentAppIntegrationsPage app={app} isDraft={false} onSaved={fetchApp} />
                        ) : (
                            // Stay in the version the user was browsing: published settings when a
                            // published snapshot exists, the draft otherwise.
                            <Navigate to={app.published ? '../published/settings' : '../draft/settings'} replace />
                        )
                    }
                />
                <Route path="sharing" element={<AliceAgentAppSharingPage app={app} />} />
                <Route path="*" element={<Navigate to="draft/settings" replace />} />
            </Routes>
        </AliceExternalAppLayout>
    );
};
